import React from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../api/useApiRequest';
import { useOnMount } from '../helpers/hooks/useOnMount';
import { assertNotNull } from '../helpers/nullHelpers';
import { LinkButton } from '../shared/Button';
import { PageLoadingIndicator } from '../shared/LoadingIndicator';
import { fadeIn } from '../styling/animations';
import { spacing06 } from '../styling/spacing';
import { Config } from './config';
import { ConfigContext } from './ConfigContext';

type Props = {
  children?: React.ReactNode;
};

export const ConfigProvider = (props: Props) => {
  const fetchConfigRequest = useApiRequest<Config>({
    httpMethod: 'get',
    endpointUrl: 'config',
    unauthenticated: true,
  });

  useOnMount(() => {
    void fetchConfigRequest.sendRequest();
  });

  if (fetchConfigRequest.inProgress) {
    return (
      <CenteredContainer>
        <PageLoadingIndicator />
      </CenteredContainer>
    );
  }

  if (fetchConfigRequest.error != null) {
    return (
      <CenteredContainer>
        <div>
          <p>Sorry, something went wrong.</p>
          <RetryButtonContainer>
            <LinkButton onClick={() => fetchConfigRequest.sendRequest()}>Try again</LinkButton>
          </RetryButtonContainer>
        </div>
      </CenteredContainer>
    );
  }

  if (fetchConfigRequest.response == null) {
    return (
      <CenteredContainer>
        <PageLoadingIndicator />
      </CenteredContainer>
    );
  }

  const getConfig = () => assertNotNull(fetchConfigRequest.response);

  return <ConfigContext.Provider value={{ getConfig }}>{props.children}</ConfigContext.Provider>;
};

const CenteredContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-in both;
`;

const RetryButtonContainer = styled.div`
  margin-top: ${spacing06};
`;
